import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Select, MenuItem, TextField, Button, Box, Typography, IconButton, Chip
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { clarities, colors } from '../../constants/Constants';
import { apiRequest } from '../../utils/apiClient';
import { useVendorContext } from '../../context/VendorContext';

type Entry = {
  id?: number | null;
  vendor: string;
  size: number;
  color: string;
  clarity: string;
  price: number;
};

const initialRow: Entry = {
  id: null,
  vendor: 'aurum',
  size: 1.5,
  color: 'EF',
  clarity: 'VVS',
  price: 20,
};

export default function SolitairePricingTable() {
  const { vendor } = useVendorContext();
  const [entries, setEntries] = useState<Entry[]>([initialRow]);

  const handleChange = (index: number, field: keyof Entry, value: any) => {
    const updated = [...entries];
    updated[index] = {
      ...updated[index],
      [field]: ['ctSize', 'ppc'].includes(field) ? parseFloat(value) || 0 : value,
    };
    setEntries(updated);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!vendor?.vendorName) return;
        const response = await apiRequest(`/lookup/solitaire/${vendor.vendorName}`);
        if (response) {
          setEntries(response);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [vendor]);

  const handleAddRow = () => {
    setEntries([...entries, { ...initialRow, vendor: vendor?.vendorName || '' }]);
  };

  const handleRemoveRow = (index: number) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    const payload = entries.map(e => ({
      ...e,
      vendor: vendor?.vendorName || '',
    }));
    apiRequest(`/lookup/solitaire/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload
    });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: '#fafafa', borderRadius: 2 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
          Solitaire Pricing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Enter carat-size rows and prices in one compact grid.
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          <Chip size="small" label={`Vendor: ${vendor?.vendorName || 'N/A'}`} variant="outlined" />
          <Chip size="small" label={`Rows: ${entries.length}`} sx={{ backgroundColor: '#fff7e0' }} />
        </Box>
      </Box>

      <Box mb={1.5} maxWidth={320}>
        <TextField
          label="Vendor"
          value={vendor?.vendorName || ''}
          disabled
          fullWidth
          size="small"
        />
      </Box>

      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        justifyContent="left"
        alignItems="flex-start"
        flex={1}
      >
        {/* Editable Table */}
        <Box flex={1} width={{ xs: '100%', md: '58%' }}>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.8, px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Ct Size</TableCell>
                  <TableCell>Clarity</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Ppc (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleRemoveRow(index)}>
                        <Remove fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={entry.size}
                        onChange={(e) => handleChange(index, 'size', e.target.value)}
                        size="small"
                        inputProps={{ step: 0.1, style: { width: 60 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={entry.clarity}
                        onChange={(e) => handleChange(index, 'clarity', e.target.value)}
                        size="small"
                        fullWidth
                      >
                        {clarities.map(c => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={entry.color}
                        onChange={(e) => handleChange(index, 'color', e.target.value)}
                        size="small"
                        fullWidth
                      >
                        {colors.map(c => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={entry.price}
                        onChange={(e) => handleChange(index, 'price', e.target.value)}
                        size="small"
                        inputProps={{ style: { width: 80 } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="space-between" mt={1.25}>
            <Button variant="outlined" size="small" onClick={handleAddRow} startIcon={<Add />} sx={{ textTransform: 'none', borderColor: '#d4af37', color: '#7a5b00' }}>
              Add Row
            </Button>
            <Button variant="contained" size="small" onClick={handleSave} sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#d4af37', color: '#000', '&:hover': { backgroundColor: '#c29a2e' } }}>
              Save
            </Button>
          </Box>
        </Box>

        {/* Price Chart */}
        {entries.length > 0 && (
          <Box flex={1} maxWidth={{ xs: '100%', md: '42%' }}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Price Chart
              </Typography>
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.8, px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell><Typography variant="body2" fontWeight="bold">Carat</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight="bold">Clarity</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight="bold">Color</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight="bold">Price per Carat (₹)</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry, i) => (
                    <TableRow key={i}>
                      <TableCell><Typography variant="body2">{entry.size} ct</Typography></TableCell>
                      <TableCell><Typography variant="body2">{entry.clarity}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{entry.color}</Typography></TableCell>
                      <TableCell><Typography variant="body2">₹{entry.price}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
}
